<template>
  <div
    id="contenedor-obras-listas-adjudicar"
    :class="sinBordes ? '' : 'rounded-lg pa-1 elevation-2'">
    <h3 class="pl-2 pt-2 d-flex align-center">
      <v-icon>
        mdi-check-all
      </v-icon>
      <span class="ml-1">
        Seleccionador de obras
        <v-chip
          small
          color="transparent"
          :disabled="obrasSeleccionadas.length <= 0">
          {{ obrasSeleccionadas.length }} obra(s) seleccionada(s)
        </v-chip>
      </span>
    </h3>
    <V-row>
      <v-col cols="2">
        <EjercicioPicker v-model="filtros.ejercicio" sinPeriodo></EjercicioPicker>
      </v-col>
      <v-col md="3">
        <v-autocomplete
          v-model="idOficio"
          :items="getOficiosAutorizacion"
          item-text="numero"
          item-value="id"
          label="Seleccionar Oficio de Autorización"
          :loading="$apollo.queries.filtrarOficiosAutorizacion.loading"
          :search-input.sync="filtros.buscarTexto"
          clearable
          hide-details
          filled
          rounded
          class="my-input"
          color="#6A83B5"
          background-color="#F2F6FF"
          @input="seleccionarOficio"
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>
                <strong>{{ item.numero }}</strong>
              </v-list-item-title>
              <v-list-item-subtitle>
                <v-icon x-small class="mr-1">mdi-calendar</v-icon>
                {{ item.fecha | formatoFecha }}
                <v-chip x-small class="ml-2" color="primary" outlined>
                  {{ item.obrasDisponibles.length }} obra(s)
                </v-chip>
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>
      <v-col cols="7">
        <v-autocomplete
          v-model="obrasSeleccionadas"
          :items="getObrasDisponibles"
          item-text="meta"
          item-value="id"
          label="Seleccionar Obras"
          :disabled="!idOficio"
          clearable
          filled
          rounded
          multiple
          chips
          small-chips
          deletable-chips
          class="my-input"
          color="#6A83B5"
          background-color="#F2F6FF"
          hide-details
          @change="handleEmiterObrasSeleccionadas"
        >
          <template v-slot:selection="{ item, index }">
            <v-chip
              v-if="index < 2"
              small
              close
              @click:close="removerObra(item)"
            >
              <span>{{ item.clave }}</span>
            </v-chip>
            <span
              v-if="index === 2"
              class="grey--text text-caption"
            >
              (+{{ obrasSeleccionadas.length - 2 }} más)
            </span>
          </template>
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>
                <strong>{{ item.clave }}</strong>
              </v-list-item-title>
              <v-list-item-subtitle class="text-caption">
                {{ item.meta }}
              </v-list-item-subtitle>
            </v-list-item-content>
          </template>
        </v-autocomplete>
      </v-col>
    </V-row>
  </div>
</template>

<script>
import OBRAS_LISTAD_ADJUDICAR from '@/graphql/querys/administracion/programacion/oficiosautorizacion/filtrarOficiosAutorizacion.gql';
import EjercicioPicker from './inputs/EjercicioPicker.vue';

export default {
  name: 'ObrasListasAdjudicar',
  props: {
    sinBordes: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    EjercicioPicker,
  },
  data() {
    return {
      filtros: {
        ejercicio: new Date().getFullYear(),
        buscarTexto: '',
        limite: 10,
      },
      idOficio: null,
      oficioSeleccionado: null,
      obrasSeleccionadas: [],
    };
  },
  apollo: {
    filtrarOficiosAutorizacion: {
      query: OBRAS_LISTAD_ADJUDICAR,
      variables() {
        return {
          filtros: {
            ejercicio: this.filtros.ejercicio,
            buscar: this.filtros.buscarTexto,
            limite: this.filtros.limite,
          },
        };
      },
      skip() {
        return !this.filtros.ejercicio;
      },
    },
  },
  computed: {
    getOficiosAutorizacion() {
      if (!this.filtrarOficiosAutorizacion) return [];
      if (!this.filtrarOficiosAutorizacion.datos) return [];
      return this.filtrarOficiosAutorizacion.datos;
    },
    getObrasDisponibles() {
      if (!this.oficioSeleccionado) return [];
      if (!this.oficioSeleccionado.obrasDisponibles) return [];

      return this.oficioSeleccionado.obrasDisponibles
        .filter((obraDisponible) => obraDisponible.obraSolicitada
          && obraDisponible.obraSolicitada.obra)
        .map((obraDisponible) => ({
          id: obraDisponible.obraSolicitada.obra.id,
          clave: obraDisponible.obraSolicitada.obra.clave,
          meta: obraDisponible.obraSolicitada.obra.meta,
          idObraSolicitada: obraDisponible.idObraSolicitada,
        }));
    },
  },
  watch: {
    'filtros.ejercicio': {
      handler() {
        // Limpiar selecciones al cambiar de ejercicio
        this.idOficio = null;
        this.oficioSeleccionado = null;
        this.obrasSeleccionadas = [];
        this.$emit('obras-seleccionadas', []);
        // Forzar refetch con el nuevo ejercicio
        this.$apollo.queries.filtrarOficiosAutorizacion.refetch();
      },
    },
  },
  methods: {
    refrescarListado() {
      // Limpiar selecciones
      this.idOficio = null;
      this.oficioSeleccionado = null;
      this.obrasSeleccionadas = [];
      this.$emit('obras-seleccionadas', []);
      // Refrescar datos del backend
      this.$apollo.queries.filtrarOficiosAutorizacion.refetch({
        filtros: {
          ejercicio: this.filtros.ejercicio,
          buscar: this.filtros.buscarTexto,
          limite: this.filtros.limite,
        },
      });
    },
    seleccionarOficio(idOficio) {
      if (idOficio) {
        const oficio = (this.filtrarOficiosAutorizacion
          && this.filtrarOficiosAutorizacion.datos
          ? this.filtrarOficiosAutorizacion.datos
          : [])
          .find((o) => o.id === idOficio);
        this.oficioSeleccionado = oficio;
        this.obrasSeleccionadas = [];
        this.$emit('oficio-seleccionado', oficio);
      } else {
        this.oficioSeleccionado = null;
        this.obrasSeleccionadas = [];
        this.$emit('oficio-seleccionado', null);
      }
    },
    removerObra(obra) {
      const index = this.obrasSeleccionadas.indexOf(obra.id);
      if (index >= 0) {
        this.obrasSeleccionadas.splice(index, 1);
      }
    },
    handleEmiterObrasSeleccionadas() {
      this.$emit('input', this.obrasSeleccionadas);
      this.$emit('obras-seleccionadas', this.getObrasDisponibles
        .filter((obra) => this.obrasSeleccionadas.includes(obra.id)));
    },
  },
  filters: {
    formatoFecha(fecha) {
      if (!fecha) return '-';
      const date = new Date(fecha);
      return date.toLocaleDateString('es-MX', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    },
  },
  mounted() {
    this.$apollo.queries.filtrarOficiosAutorizacion.refetch();
  },
};
</script>

<style lang="scss">
.my-input.v-input .v-input__slot {
  border-radius: 10px;
}
</style>
